/** Lines and words. The highlight moves one whole word at a time. */
export const layoutVerse = (poem) => {
  let cursor = 0;
  const lines = String(poem || '').trim().split('\n').map((line) => {
    const parts = [];
    String(line).split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (!part.trim()) {
        parts.push({ kind: 'gap', text: part });
        return;
      }
      if (!/\p{L}/u.test(part)) {
        parts.push({ kind: 'mark', text: part });
        return;
      }
      parts.push({ kind: 'word', text: part, start: cursor });
      cursor += 1;
    });
    return parts;
  });
  return { lines, count: cursor };
};

const quietFloor = (values) => {
  if (!values.length) return 0.02;
  const sample = [];
  const step = Math.max(1, Math.floor(values.length / 400));
  for (let i = 0; i < values.length; i += step) sample.push(values[i]);
  sample.sort((a, b) => a - b);
  const low = sample[Math.floor(sample.length * 0.2)] || 0;
  const mid = sample[Math.floor(sample.length * 0.55)] || 0;
  return Math.max(low * 1.35, mid * 0.2, 0.006);
};

/**
 * Place each sung syllable on a rise in this clip.
 * Silence between lines does not advance the text.
 */
export const syllableTimeline = (values, hop, count) => {
  const frames = Array.isArray(values) ? values : [];
  const step = Number(hop) > 0 ? Number(hop) : 0.05;
  const duration = Math.max(step, frames.length * step);
  if (!count) return [];
  if (frames.length < 8) {
    return Array.from({ length: count }, (_, index) => (index / count) * duration);
  }

  const smooth = frames.map((value, index) => {
    let sum = 0;
    let seen = 0;
    for (let offset = -2; offset <= 2; offset += 1) {
      const sample = frames[index + offset];
      if (sample == null) continue;
      sum += sample;
      seen += 1;
    }
    return seen ? sum / seen : value;
  });
  const quiet = quietFloor(smooth);
  const minGap = Math.max(1, Math.round(0.12 / step));
  const onsets = [];
  for (let index = 1; index < smooth.length - 1; index += 1) {
    if (smooth[index] < quiet) continue;
    const rising = smooth[index] >= smooth[index - 1] && smooth[index] > smooth[index + 1];
    if (!rising) continue;
    if (!onsets.length || index - onsets[onsets.length - 1] >= minGap) onsets.push(index);
    else if (smooth[index] > smooth[onsets[onsets.length - 1]]) onsets[onsets.length - 1] = index;
  }

  const voiced = [];
  for (let index = 0; index < smooth.length; index += 1) {
    if (smooth[index] > quiet) voiced.push(index);
  }
  const acrossVoiced = () => {
    if (voiced.length < 4) {
      return Array.from({ length: count }, (_, index) => (index / count) * duration);
    }
    return Array.from({ length: count }, (_, index) => {
      const at = Math.min(voiced.length - 1, Math.floor((index / count) * voiced.length));
      return voiced[at] * step;
    });
  };

  const usable = onsets.length >= Math.max(3, count * 0.5) && onsets.length <= count * 1.85;
  if (!usable) return acrossVoiced();
  if (count === 1) return [onsets[0] * step];
  return Array.from({ length: count }, (_, index) => {
    const pos = (index / (count - 1)) * (onsets.length - 1);
    const left = Math.floor(pos);
    const right = Math.min(onsets.length - 1, left + 1);
    const mix = pos - left;
    return (onsets[left] * (1 - mix) + onsets[right] * mix) * step;
  });
};

export const DEFAULT_TEXT_SPEED = 1;

export const clampTextSpeed = (value) => {
  const speed = Number(value);
  if (!Number.isFinite(speed) || speed <= 0) return DEFAULT_TEXT_SPEED;
  const stepped = Math.round(speed * 20) / 20;
  return Math.min(2.5, Math.max(0.4, stepped));
};

/** 1× finishes the text as the clip ends. Higher moves the highlight faster. */
export const syllableIndexForProgress = (count, progress, textSpeed = DEFAULT_TEXT_SPEED) => {
  const total = Number(count) || 0;
  if (total < 1) return -1;
  const paced = Math.min(0.999, Math.max(0, Number(progress) || 0) * clampTextSpeed(textSpeed));
  return Math.min(total - 1, Math.floor(paced * total));
};

export const activeSyllableAt = (marks, seconds) => {
  if (!marks?.length) return -1;
  const now = Number(seconds) || 0;
  let active = 0;
  for (let index = 0; index < marks.length; index += 1) {
    if (now + 0.04 >= marks[index]) active = index;
    else break;
  }
  return active;
};
